import { useEffect, useState } from 'react'

const TICK_MS = 30_000

/**
 * Milliseconds until `deadline`, or null when there is no usable deadline.
 * Ticks per half minute because the display is "6h 12m"; a per-second tick would
 * re-render for nothing.
 */
export const useCountdown = (deadline: string | undefined): number | null => {
  const [now, setNow] = useState(() => Date.now())

  const deadlineMs = deadline ? new Date(deadline).getTime() : NaN
  const expired = !Number.isNaN(deadlineMs) && deadlineMs <= now

  useEffect(() => {
    // Nothing left to count: keep ticking and the page re-renders every half
    // minute for as long as it stays open.
    if (!deadline || expired) {
      return undefined
    }
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [deadline, expired])

  if (!deadline || Number.isNaN(deadlineMs)) {
    return null
  }
  return Math.max(0, deadlineMs - now)
}

/**
 * Renders remaining milliseconds as whole hours ("47 hours") until under an hour
 * is left, then as minutes ("45 minutes"). Minutes only earn their place once
 * they are the difference between acting today and missing the offer.
 *
 * Hours floor and minutes round up: "47 hours" never overstates the time left,
 * and a live offer with forty seconds on it reads "1 minute" rather than sitting
 * at "0 minutes" beside a price of $0.
 */
export const formatRemaining = (ms: number): string => {
  // Floored from ms, not from a rounded-up minute count: rounding to minutes
  // first carries 1h59m30s to 120 minutes and prints "2 hours".
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`
  const minutes = Math.ceil(ms / 60_000)
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}
